import React, { useState } from "react";
import { createVibe } from "../../api/categories";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Review = ({ formData, goBack, onFinish }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [extraData, setExtraData] = useState({
    note1: "",
    note2: "",
    note3: "",
  });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      // If dateString is in YYYY-MM-DD format
      if (dateString.includes("-") && dateString.length === 10) {
        const parts = dateString.split("-");
        return `${parts[1]}/${parts[2]}/${parts[0]}`;
      }
      // If already in MM/DD/YYYY format, return as is
      if (dateString.includes("/")) {
        return dateString;
      }
      // Try to parse as Date object
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      }
      return dateString;
    } catch (e) {
      return dateString || "N/A";
    }
  };

  const handleChange = (e) => {
    setExtraData({ ...extraData, [e.target.name]: e.target.value });
  };

  const handleSendVibe = async () => {
    setLoading(true);
    try {
      const { vibeType, ...rest } = formData;

      let vibeCategoryId = formData.vibe_category_id;
      if (
        !vibeCategoryId &&
        formData.selectedCategory &&
        formData.selectedCategory.id
      ) {
        vibeCategoryId = formData.selectedCategory.id;
      }

      let addressId = formData.address_id;
      if (!addressId && formData.selectedPlace?.id) {
        addressId = formData.selectedPlace.id;
      }

      let members = Array.isArray(formData.members)
        ? formData.members.map((m) =>
            typeof m === "object" ? m.user_id || m.id : m
          )
        : [];

      if (!formData.id) {
        toast.error(t("Error_Vibe_id_is_required"));
        setLoading(false);
        return;
      }

      const payload = {
        id: formData.id,
        steps: 6,
        attire: extraData.note1 || "",
        chip_in: extraData.note2 || "",
        venue_incentive: extraData.note3 || "",
        venue:
          typeof formData.venue === "string"
            ? formData.venue
            : formData.selectedPlace?.name || formData.address || "",
      };

      const response = await createVibe(payload);
      setLoading(false);

      if (response.success) {
        toast.success(t("vibe_sent_successfully"));
        onFinish();
        navigate("/my-vibes");
      } else {
        toast.error(response.message || t("failed_to_send_vibe"));
      }
    } catch (err) {
      setLoading(false);
      toast.error("Failed to send vibe. Please try again.");
    }
  };

  return (
    <>
      {/* Header */}
      <div className="flex justify-between items-center">
        <button
          onClick={goBack}
          className="py-2 rounded-lg font-semibold text-gray-700 dark:text-white"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>

        <h1
          className="text-sm md:text-lg lg:text-xl font-bold"
          style={{ color: "#6d2af6" }}
        >
          {t("vibe_card")}
        </h1>

        <div />
      </div>

      {/* Title */}
      {formData.vibeName && (
        <h1 className="text-3xl md:text-4xl font-bold text-center mb-8 text-gray-800 dark:text-white">
          {formData.vibeName}
        </h1>
      )}

      {/* Main Card */}
      <div className="flex justify-center items-start w-full overflow-hidden">
        <div className="bg-white dark:bg-[#252739] rounded-3xl shadow-xl py-8 px-3 w-full max-w-lg  min-h-fit overflow-hidden">

          {/* ⭐ INFO GRID – ALWAYS 2 COLUMNS (Vibe/Links vs Place/Date+Time) */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            {/* LEFT COLUMN: Vibe + Links */}
            <div className="space-y-5 pl-4">
              {/* The Vibe */}
              <div>
                <div className="text-gray-500 dark:text-dark-text-secondary font-medium text-base mb-2">
                  {t("the_vibe")}
                </div>
                <div className="font-bold text-gray-800 dark:text-dark-text text-lg leading-relaxed">
                  {formData.vibeName || "N/A"}
                </div>
              </div>

              {/* The Links */}
              <div>
                <div className="text-gray-500 dark:text-dark-text-secondary font-medium text-base mb-2">
                  {t("the_links")}
                </div>
                <div className="font-bold text-gray-800 dark:text-dark-text text-lg leading-relaxed">
                  {Array.isArray(formData.vibe_links) &&
                  formData.vibe_links.length > 0
                    ? formData.vibe_links.map((link) => link.name).join(", ")
                    : "Colleagues, Friends, Family, Besties"}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Place + Date/Time */}
            <div className="space-y-5">
              {/* The Place */}
              <div>
                <div className="text-gray-500 dark:text-dark-text-secondary font-medium text-base mb-2">
                  {t("the_place")}
                </div>
                <div className="font-bold text-gray-800 dark:text-dark-text text-lg leading-relaxed">
                  {formData.selectedPlace?.name || formData.address || "N/A"}
                </div>
              </div>

              {/* Date + Time */}
              <div>
                <div className="text-gray-500 dark:text-dark-text-secondary font-medium text-base mb-2">
                  {t("date_time", "Date & Time")}
                </div>
                <div className="font-bold text-gray-800 dark:text-dark-text text-lg leading-relaxed">
                  {formData.date && formData.time
                    ? `${formatDate(formData.date)} ${formData.time}`
                    : formData.date
                    ? formatDate(formData.date)
                    : "N/A"}
                </div>
              </div>
            </div>
          </div>

          {/* Input Fields with Card Design */}
          <div className="space-y-5">
            {/* Attire */}
            <div className="flex items-center justify-center">
              <span className="text-gray-600 dark:text-dark-text-secondary font-medium text-base mr-4 w-32">
                {t("attire")}
              </span>
              <div className="bg-blue-100 rounded-2xl px-5 py-2 min-w-[140px] text-center">
                <input
                  type="text"
                  name="note1"
                  value={extraData.note1}
                  onChange={handleChange}
                  className="bg-transparent text-center font-medium outline-none border-none w-full text-base focus:outline-none focus:ring-0 placeholder:text-gray-600"
                  placeholder={t("casual", "Casual")}
                  style={{ color: "#000000", WebkitTextFillColor: "#000000" }}
                />
              </div>
            </div>

            {/* Chip In */}
            <div className="flex items-center justify-center">
              <span className="text-gray-600 dark:text-dark-text-secondary font-medium text-base mr-4 w-32">
                {t("chip_in", "Chip In")}
              </span>
              <div className="bg-green-100 rounded-2xl px-5 py-2 min-w-[140px] text-center">
                <input
                  type="text"
                  name="note2"
                  value={extraData.note2}
                  onChange={handleChange}
                  className="bg-transparent text-center font-medium outline-none border-none w-full text-base focus:outline-none focus:ring-0 placeholder:text-gray-600"
                  placeholder="0"
                  style={{ color: "#000000", WebkitTextFillColor: "#000000" }}
                />
              </div>
            </div>

            {/* Venue Incentive */}
            <div className="flex items-center justify-center">
              <span className="text-gray-600 dark:text-dark-text-secondary font-medium text-base mr-4 w-32">
                {t("venue_incentive", "Venue Incentive")}
              </span>
              <div className="bg-purple-100 rounded-2xl px-5 py-2 min-w-[140px] text-center">
                <input
                  type="text"
                  name="note3"
                  value={extraData.note3}
                  onChange={handleChange}
                  className="bg-transparent text-center font-medium outline-none border-none w-full text-base focus:outline-none focus:ring-0 placeholder:text-gray-600"
                  placeholder="0"
                  style={{ color: "#000000", WebkitTextFillColor: "#000000" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send Button */}
      <div className="flex justify-center w-full mt-6">
        <button
          onClick={handleSendVibe}
          disabled={loading}
          className={`w-full max-w-lg p-3 rounded-lg font-semibold text-white text-base ${
            loading ? "bg-purple-400" : "bg-[#6d29f6]"
          } transition-colors duration-200 shadow-lg`}
        >
          {loading ? t("sending") : t("send_vibe")}
        </button>
      </div>
    </>
  );
};

export default Review;
